import { useState } from "react";
import type { PhaseTaps, PrimaryWorkAction, StartGateController } from "../../../features/work-session";
import { useSuccessDialog } from "../../../shared/hooks/useSuccessDialog";

export type TaskFlow = "complete" | "end-departure" | "plan" | null;

type Args = {
  taps: PhaseTaps;
  /** A start on an item the office is rescheduling opens its dialog instead of firing. */
  startGate: StartGateController;
};

/**
 * Every modal / overlay / success-dialog transition on the task page, collapsed onto one `flow`
 * value — the counterpart of `useAppointmentFlows`. Tasks have no follow-up concept, so there is
 * no "Folgetermin"/"reschedule" branch: "plan" covers both first scheduling and replanning, the
 * same way the page's "Im Kalender planen" / "Umdisponieren" button always opens the same overlay.
 */
export function useTaskFlows({ taps, startGate }: Args) {
  const [flow, setFlow] = useState<TaskFlow>(null);
  const { showSuccess, dialogProps } = useSuccessDialog();

  const close = () => setFlow(null);

  /** Opening actions are pure taps; closing ones open their sheet. */
  const onPrimaryAction = (action: PrimaryWorkAction) => {
    if (action === "complete") setFlow("complete");
    else if (action === "end-departure") setFlow("end-departure");
    else if (!startGate.intercept(action)) taps.run(action);
  };

  const onPlan = () => setFlow("plan");

  // Kept as the two distinct messages the page already showed — unlike the appointment page's
  // three session-close modals, the task's "Abfahrt beenden" and "Arbeit abschließen" never
  // shared one message.
  const onDepartureSaved = () => {
    close();
    showSuccess("Abfahrt gespeichert");
  };

  const onCompleted = () => {
    close();
    showSuccess("Aufgabe als erledigt markiert");
  };

  const onPlanned = () => {
    close();
    showSuccess("Aufgabe erfolgreich geplant");
  };

  return {
    flow,
    close,
    onPrimaryAction,
    onPlan,
    onDepartureSaved,
    onCompleted,
    onPlanned,
    success: dialogProps,
  };
}

export type TaskFlows = ReturnType<typeof useTaskFlows>;
